import React from "react"

import { ApiQueryName } from "@/models/api"
import type { User } from "@/models/user"
import { type UserRoleType, isCredentialManagerRole } from "@/models/user-role"
import { getCurrentAppUrl } from "@/utils/urls"
import { datadogRum } from "@datadog/browser-rum"
import {
  type UseMutateAsyncFunction,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"

import {
  classifyPostLoginUserFailure,
  classifySignInFailure,
  doLogin,
  doLogout,
  fetchSessionValidity,
  getUser,
} from "./authentication-context-utils"
import type { SignInResult } from "./authentication-context-utils"

interface AuthenticationContextValue {
  activeRole: UserRoleType | null
  activeUser: User | null | undefined
  authenticatedUser: User | null | undefined
  authenticatedUserError: unknown
  canEditSite: boolean | undefined
  canManageCredentials: boolean | undefined
  isAdminUser: boolean
  isAdminWriterUser: boolean
  isAuthenticated: boolean | undefined
  isAuthenticatedUserFetched: boolean
  isAuthenticatedUserFetching: boolean
  isAuthenticatedUserLoading: boolean
  isAuthenticating: boolean
  isGetAuthenticatedUserLoading: boolean
  isImpersonatingRole: boolean
  isLoading: boolean
  isNZeroOSUser: boolean
  isOrgCredentialsAdminUser: boolean
  isOrgAdminUser: boolean
  isSessionValidFetched: boolean
  isSessionValidFetching: boolean
  isSessionValidLoading: boolean
  isUserLoading: boolean
  refetchAuthenticatedUser: () => Promise<unknown>
  refetchSessionValidity: () => Promise<unknown>
  sessionError: unknown
  setActiveRole: React.Dispatch<React.SetStateAction<UserRoleType | null>>
  setActiveUser: React.Dispatch<React.SetStateAction<User | null | undefined>>
  setIsImpersonatingRole: React.Dispatch<React.SetStateAction<boolean>>
  signIn: UseMutateAsyncFunction<
    SignInResult,
    unknown,
    {
      email: string
      password: string
    }
  >
  signOut: UseMutateAsyncFunction<void>
}

interface AuthenticationProviderProps extends Partial<AuthenticationContextValue> {
  children: React.ReactNode
}

const AuthenticationContext = React.createContext<AuthenticationContextValue>({
  activeRole: null,
  activeUser: null,
  authenticatedUser: null,
  authenticatedUserError: null,
  canEditSite: undefined,
  canManageCredentials: undefined,
  isAdminUser: false,
  isAdminWriterUser: false,
  isAuthenticated: undefined,
  isAuthenticatedUserFetched: false,
  isAuthenticatedUserFetching: false,
  isAuthenticatedUserLoading: false,
  isAuthenticating: false,
  isGetAuthenticatedUserLoading: false,
  isImpersonatingRole: false,
  isLoading: false,
  isNZeroOSUser: false,
  isOrgCredentialsAdminUser: false,
  isOrgAdminUser: false,
  isSessionValidFetched: false,
  isSessionValidFetching: false,
  isSessionValidLoading: false,
  isUserLoading: false,
  refetchAuthenticatedUser: () => Promise.resolve(undefined),
  refetchSessionValidity: () => Promise.resolve(undefined),
  sessionError: null,
  setActiveRole: () => undefined,
  setActiveUser: () => undefined,
  setIsImpersonatingRole: () => undefined,
  signIn: () => Promise.resolve("invalid_credentials"),
  signOut: () => Promise.resolve(undefined),
})

export const isSessionValidQueryKey: string[] = [
  ApiQueryName.IsSessionValid,
] as const

export const getAuthenticatedUserQueryKey: string[] = [
  ApiQueryName.Authentication,
  "getAuthenticatedUser",
] as const

const signInMutationKey = [ApiQueryName.Authentication, "signIn"] as const
const signOutMutationKey = [ApiQueryName.Authentication, "signOut"] as const

/**
 * Provides the authentication context
 *
 * @example
 * return (
 *   <AuthenticationProvider>
 *     <Component />
 *   </AuthenticationProvider>
 * )
 */
export const AuthenticationProvider: React.FC<AuthenticationProviderProps> = (
  props
) => {
  const queryClient = useQueryClient()

  const [activeRole, setActiveRole] =
    React.useState<AuthenticationContextValue["activeRole"]>(null)
  const [isImpersonatingRole, setIsImpersonatingRole] = React.useState(false)

  const [activeUser, setActiveUser] =
    React.useState<AuthenticationContextValue["activeUser"]>(undefined)

  const signOutMutation = React.useCallback(async (): Promise<void> => {
    try {
      const currentUrl = getCurrentAppUrl()
      const encodedCurrentUrl = encodeURIComponent(currentUrl)

      await doLogout()
      datadogRum.stopSessionReplayRecording()
      datadogRum.stopSession()
      globalThis.location.replace(`/session_timeout?from=${encodedCurrentUrl}`)
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error(error)
      }
    }
  }, [])

  const pollSessionMinutes =
    Number.parseFloat(
      process.env.REACT_APP_EXPIRATION_CHECK_INTERVAL_MINUTES ?? "5"
    ) || 5
  const pollSessionMilliseconds = pollSessionMinutes * 60 * 1000

  const {
    data: isSessionValid,
    error: sessionError,
    isFetched: isSessionValidFetched,
    isFetching: isSessionValidFetching,
    isLoading: isSessionValidLoading,
    refetch: refetchSessionValidity,
  } = useQuery({
    gcTime: Infinity,
    meta: { skipAuthRedirect: true },
    queryKey: isSessionValidQueryKey,
    queryFn: fetchSessionValidity,
    refetchInterval: pollSessionMilliseconds,
    staleTime: Infinity,
  })

  const {
    data: authenticatedUser,
    error: authenticatedUserError,
    isFetched: isAuthenticatedUserFetched,
    isFetching: isAuthenticatedUserFetching,
    isLoading: isAuthenticatedUserLoading,
    refetch: refetchAuthenticatedUser,
  } = useQuery({
    gcTime: Infinity,
    meta: { skipAuthRedirect: true },
    queryKey: getAuthenticatedUserQueryKey,
    queryFn: () => getUser(),
    staleTime: Infinity,
  })

  const { mutateAsync: signOut } = useMutation({
    mutationKey: signOutMutationKey,
    mutationFn: signOutMutation,
  })

  const { mutateAsync: signIn, isPending: isAuthenticating } = useMutation({
    meta: { handlesOwnErrors: true },
    mutationKey: signInMutationKey,
    mutationFn: async (credentials: {
      email: string
      password: string
    }): Promise<SignInResult> => {
      await queryClient.cancelQueries({
        queryKey: getAuthenticatedUserQueryKey,
      })

      let loginResult
      try {
        loginResult = await doLogin({ data: credentials })
      } catch (error: unknown) {
        return classifySignInFailure(error)
      }

      if (!loginResult.success) {
        return "invalid_credentials"
      }

      // Combine getUser and signIn so we don't have a re-render where both return isLoading: false
      let userResult: User
      try {
        userResult = await getUser()
      } catch (error: unknown) {
        return classifyPostLoginUserFailure(error)
      }

      queryClient.setQueryData(isSessionValidQueryKey, true)
      queryClient.setQueryData(getAuthenticatedUserQueryKey, userResult)

      return "success"
    },
  })

  // Automatically sign out when a fetched session result is invalid while signed in
  React.useEffect(() => {
    if (authenticatedUser && isSessionValid === false) {
      void signOut()
    }
  }, [authenticatedUser, isSessionValid, signOut])

  // Reset the active user and active role when the authenticated user changes
  // This sync pattern is intentional
  React.useEffect(() => {
    if (!authenticatedUser) {
      return
    }

    // eslint-disable-next-line @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect
    setActiveUser(authenticatedUser)

    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setActiveRole(authenticatedUser.role)

    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setIsImpersonatingRole(false)
  }, [authenticatedUser])

  const canEditSite: boolean =
    activeRole === "admin" || activeRole === "org_admin"

  const isGetAuthenticatedUserLoading =
    isSessionValidLoading || isAuthenticatedUserLoading || isAuthenticating

  // This is purposely a tri-state boolean
  // When the app first loads, we don't know if a user is authenticated until the api returns
  const isAuthenticated: boolean | undefined = isGetAuthenticatedUserLoading
    ? undefined
    : isSessionValid && !!authenticatedUser

  const isAdminUser = activeRole === "admin" || activeRole === "admin_reader"
  const isAdminWriterUser = activeRole === "admin"

  const isNZeroOSUser =
    activeRole === "admin" ||
    activeRole === "admin_reader" ||
    activeRole === "consultant_user" ||
    activeRole === "org_utility_credentials_admin" ||
    activeRole === "org_admin"

  const isOrgCredentialsAdminUser =
    activeRole === "org_utility_credentials_admin"
  const isOrgAdminUser = activeRole === "org_admin"

  const canManageCredentials = isCredentialManagerRole(activeRole)

  const value: AuthenticationContextValue = React.useMemo(
    () => ({
      activeRole,
      activeUser,
      authenticatedUser,
      authenticatedUserError,
      canEditSite,
      canManageCredentials,
      isAdminUser,
      isAdminWriterUser,
      isAuthenticated,
      isAuthenticatedUserFetched,
      isAuthenticatedUserFetching,
      isAuthenticatedUserLoading,
      isAuthenticating,
      isImpersonatingRole,
      isLoading: isGetAuthenticatedUserLoading,
      isNZeroOSUser,
      isOrgCredentialsAdminUser,
      isOrgAdminUser,
      isGetAuthenticatedUserLoading,
      isUserLoading: isGetAuthenticatedUserLoading,
      isSessionValidFetched,
      isSessionValidFetching,
      isSessionValidLoading,
      refetchAuthenticatedUser,
      refetchSessionValidity,
      sessionError,
      setActiveRole,
      setActiveUser,
      setIsImpersonatingRole,
      signIn,
      signOut,
    }),
    [
      activeRole,
      activeUser,
      authenticatedUser,
      authenticatedUserError,
      canEditSite,
      canManageCredentials,
      isAdminUser,
      isAdminWriterUser,
      isAuthenticated,
      isAuthenticatedUserFetched,
      isAuthenticatedUserFetching,
      isAuthenticatedUserLoading,
      isAuthenticating,
      isGetAuthenticatedUserLoading,
      isImpersonatingRole,
      isNZeroOSUser,
      isOrgCredentialsAdminUser,
      isOrgAdminUser,
      isSessionValidFetched,
      isSessionValidFetching,
      isSessionValidLoading,
      refetchAuthenticatedUser,
      refetchSessionValidity,
      sessionError,
      setActiveRole,
      setActiveUser,
      setIsImpersonatingRole,
      signIn,
      signOut,
    ]
  )

  return (
    <AuthenticationContext value={value}>
      {props.children}
    </AuthenticationContext>
  )
}

/**
 * Provides authentication
 *
 * @example
 * const { authenticatedUser, activeUser, setActiveUser } = useAuthentication()
 */
export const useAuthentication = () => React.use(AuthenticationContext)
